/**
 * Headless force-directed layout, mirroring the guide's
 * "deterministic circular seed → force refine" pipeline.
 *
 * Uses d3-force (re-exported from the already-installed `d3` package — no new
 * dependency) to compute node positions synchronously (no animation), then
 * returns a plain `{ id → {x, y} }` map that either renderer can apply.
 */

import { forceSimulation, forceManyBody, forceLink, forceCollide, forceCenter } from 'd3';

export interface LayoutNodeInput {
  id: string;
}

export interface LayoutEdgeInput {
  source: string;
  target: string;
}

export interface LayoutOptions {
  width?: number;
  height?: number;
  /** Number of synchronous ticks to run (the guide uses ~250). */
  iterations?: number;
  /** Preferred edge length. */
  linkDistance?: number;
  /** Many-body charge (negative = repulsion). */
  charge?: number;
  /** Minimum spacing between node centers (raise for large card nodes). */
  collideRadius?: number;
}

interface SimNode {
  id: string;
  x: number;
  y: number;
}

/**
 * Compute a stable force-directed layout. Nodes are seeded on a deterministic
 * circle first (so the result is reproducible run-to-run) and then relaxed by
 * the simulation. Self-loops and edges to unknown nodes are ignored.
 */
export function computeForceLayout(
  nodes: LayoutNodeInput[],
  edges: LayoutEdgeInput[],
  options: LayoutOptions = {}
): Record<string, { x: number; y: number }> {
  const {
    width = 1600,
    height = 1000,
    iterations = 300,
    linkDistance = 180,
    charge = -800,
    collideRadius = 90,
  } = options;

  const positions: Record<string, { x: number; y: number }> = {};
  if (nodes.length === 0) return positions;

  const cx = width / 2;
  const cy = height / 2;
  const radius = Math.min(width, height) * 0.4;

  // Deterministic circular seed positions.
  const simNodes: SimNode[] = nodes.map((n, i) => {
    const angle = (i / nodes.length) * 2 * Math.PI;
    return { id: n.id, x: cx + radius * Math.cos(angle), y: cy + radius * Math.sin(angle) };
  });

  const nodeIds = new Set(simNodes.map((n) => n.id));
  const simLinks = edges
    .filter((e) => e.source !== e.target && nodeIds.has(e.source) && nodeIds.has(e.target))
    .map((e) => ({ source: e.source, target: e.target }));

  const simulation = forceSimulation(simNodes as never[])
    .force('charge', forceManyBody().strength(charge))
    .force(
      'link',
      forceLink(simLinks as never[])
        .id((d: unknown) => (d as SimNode).id)
        .distance(linkDistance)
    )
    .force('collide', forceCollide(collideRadius))
    .force('center', forceCenter(cx, cy))
    .stop();

  for (let i = 0; i < iterations; i++) simulation.tick();

  for (const n of simNodes) {
    positions[n.id] = { x: n.x, y: n.y };
  }
  return positions;
}
